import {
  FormField,
  GeckoMeasurementSystem,
  GeckoMeasurementUnit,
  RolodexEntryPutJson,
} from 'types'

export enum FeatureFormMode {
  Add,
  Edit,
}

export interface FormFieldsByUniqueID {
  uniqueID: string
  formFields: FormField[]
}

export type SimpleFormField = {
  [key: string]: string | string[] | undefined
}

export interface PermissionsCheck {
  userPermissions: ReadonlySet<string> | undefined
  permissionsChecker: (
    formField: FormField,
    userPermissions?: ReadonlySet<string> | undefined
  ) => { canEditField: boolean; canReadField: boolean }
}

export interface MeasurementApplication {
  measurementSystem: GeckoMeasurementSystem
  measurementApplication: (
    formField: FormField,
    measurementSystem: GeckoMeasurementSystem
  ) => {
    fieldValue: string | number | string[] | undefined
    fieldUnits: string
    units: GeckoMeasurementUnit | null
  }
}

export type HandleSaveToRolodexType = (
  entries: RolodexEntryPutJson[],
  forceRefresh: () => Promise<void>,
  formID?: string
) => void

/**
 * Writes `metricValue` — the field's METRIC BASE UNIT (e.g. MPa for a `PsiOrMPa`
 * field) — into the field a `renderFieldAccessory` accessory sits beside, and
 * reflects it in the otherwise-uncontrolled input box. Matches the metric-base
 * scalar a manual edit stores, so it round-trips on save. Handed to an inline
 * helper (e.g. the FFS material allowable-stress picker) so it can set the value
 * programmatically.
 */
export type SetFieldAccessoryValue = (metricValue: number) => void

/**
 * A caller-scoped override for a single asset-model form field, keyed by
 * `dataKey`. Lets a workflow relabel a field and/or present a transformed
 * quantity WITHOUT editing the shared component schema. Used by the FFS step to
 * show "Allowable Stress" / "Diameter" while the stored keys stay
 * `yield_strength` / `radius` (a true outer radius).
 *
 * `toDisplay`/`fromDisplay` operate on the field's METRIC-BASE numeric value
 * (the raw quantity, e.g. meters — NOT the user's display unit), which the form
 * then unit-converts for the box. They must be exact inverses. Example for
 * radius→diameter: `toDisplay: r => r * 2`, `fromDisplay: d => d / 2`. Because
 * the factor is a pure scalar it is unit-independent; the box shows the doubled
 * quantity in whatever unit the user has selected, and the inverse re-applies
 * before serialization so the stored `radius` is unchanged.
 *
 * These are intentionally plain-number transforms, NOT `GeckoMeasurement`s: they
 * act only on the metric-base magnitude and must be unit-independent pure
 * functions, so there is no unit for a caller to "forget to transform" — the
 * surrounding form owns all unit conversion.
 */
export interface FormFieldOverride {
  /** Replaces the field's display label. */
  label?: string
  /** Maps the stored metric-base value to the value shown/edited in the form. */
  toDisplay?: (metricValue: number) => number
  /** Inverse of `toDisplay`, applied to the edited metric-base value on save. */
  fromDisplay?: (metricValue: number) => number
  /**
   * Replaces the field's `unit_magnitude`, letting a workflow present the value in a
   * different unit family (e.g. FFS shows radius/diameter in inches|mm, not ft|m). The
   * form derives BOTH displayed value and unit label from `unit_magnitude`.
   */
  unit_magnitude?: FormField['unit_magnitude']
}

/** Field overrides keyed by `dataKey`. Fields absent from the map are untouched. */
export type FormFieldOverrideMap = Record<string, FormFieldOverride>
